import axios, { type AxiosRequestConfig } from 'axios';
import { runtimeConfig } from '@/config/runtime';
import i18n from '@/i18n';
import { useAuthStore } from '@/auth/authStore';
import type { LoginResponse } from './types';
import { toApiError } from './errors';

/** Raw client without auth interceptors (login/refresh/logout). Errors are still normalized to ApiError. */
export const authHttp = axios.create({ baseURL: runtimeConfig.apiBaseUrl, withCredentials: true });
authHttp.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18n.language;
  return config;
});
authHttp.interceptors.response.use(undefined, (error) => Promise.reject(toApiError(error)));

/** Main API client: bearer token, language, request id, transparent single-flight token refresh. */
export const http = axios.create({
  baseURL: runtimeConfig.apiBaseUrl,
  withCredentials: true,
  // Spring expects repeated params: sort=a,asc&sort=b,desc (not sort[]=...)
  paramsSerializer: { indexes: null },
});

http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Accept-Language'] = i18n.language;
  config.headers['X-Request-Id'] = crypto.randomUUID();
  return config;
});

let refreshing: Promise<string> | null = null;

export async function refreshSession(): Promise<LoginResponse> {
  const { data } = await authHttp.post<LoginResponse>('/v1/auth/refresh');
  useAuthStore.getState().setSession(data);
  return data;
}

function refreshOnce(): Promise<string> {
  refreshing ??= refreshSession()
    .then((r) => r.accessToken)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

http.interceptors.response.use(undefined, async (error) => {
  const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
  if (error.response?.status === 401 && original && !original._retry) {
    original._retry = true;
    try {
      const token = await refreshOnce();
      original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
      return http(original);
    } catch {
      useAuthStore.getState().clear();
      if (!window.location.pathname.startsWith('/login')) {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?expired=1&returnTo=${returnTo}`);
      }
    }
  }
  return Promise.reject(toApiError(error));
});
